import { ParsedUrlQuery } from 'querystring'
import { useRouter } from 'next/router'
import { useCallback } from 'react'

type QueryModifier = (currentQuery: ParsedUrlQuery) => ParsedUrlQuery

type PaginationParams = {
  query: ParsedUrlQuery
  updateQuery: (
    queryModifier: QueryModifier,
    options?: { replace?: boolean; shallow?: boolean },
  ) => void
  buildSWRUrl: (endpoint: string, params: Partial<ParsedUrlQuery>) => string
  resetFilters: () => void
  reset: () => void
}

export const usePaginationParams = (): PaginationParams => {
  const router = useRouter()
  const typedQuery = router.query as ParsedUrlQuery

  const removeNullValues = (obj: Record<string, any>): Record<string, any> => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value !== null),
    )
  }

  const updateQuery = useCallback(
    (
      queryModifier: QueryModifier,
      options: {
        replace?: boolean
        shallow?: boolean
      } = {},
    ) => {
      const { replace = true, shallow = true } = options
      const modifiedQuery = queryModifier(typedQuery)
      const cleanedQuery = removeNullValues(modifiedQuery)

      const href = {
        pathname: router.pathname,
        query: cleanedQuery,
      }

      if (replace) {
        router.replace(href, undefined, { shallow })
      } else {
        router.push(href, undefined, { shallow })
      }
    },
    [typedQuery],
  )

  const buildSWRUrl = useCallback(
    (endpoint: string, params: Partial<ParsedUrlQuery>): string => {
      const searchParams = new URLSearchParams()

      Object.entries(params).forEach(([key, value]) => {
        if (value != null) {
          searchParams.append(key, value.toString())
        }
      })

      const queryString = searchParams.toString()
      return queryString ? `${endpoint}?${queryString}` : endpoint
    },
    [typedQuery],
  )

  const resetFilters = useCallback(() => {
    updateQuery((query: ParsedUrlQuery) => ({
      ...typedQuery,
      tags: [],
      users: undefined,
    }))
  }, [typedQuery])

  const reset = useCallback(() => {
    updateQuery((query: ParsedUrlQuery) => ({
      ...typedQuery,
      search: null,
      tags: [],
      users: undefined,
    }))
  }, [typedQuery])

  return {
    query: typedQuery,
    updateQuery,
    buildSWRUrl,
    resetFilters,
    reset,
  }
}
